import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CourseConfig } from '@/types/course.types';
import { ShotData } from '@/types/shot.types';
import { CameraPreset } from '@/types/camera.types';
import { CameraController } from '@/three/camera/CameraController';
import { ShotTracer } from '@/three/objects/ShotTracer';
import { TubeShotTracer } from '@/three/objects/TubeShotTracer';
import { BaseShotTracer } from '@/three/objects/BaseShotTracer';
import { latLngToLocalFromObject } from '@/three/utils/coordinateUtils';

/**
 * Main scene class managing the three.js scene, camera, lighting, and course terrain
 */
export class CourseScene {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private cameraController: CameraController;
  private controls: OrbitControls;
  private courseConfig: CourseConfig;
  private activeShots: Map<string, BaseShotTracer>;
  private clock: THREE.Clock;

  constructor(canvas: HTMLCanvasElement, courseConfig: CourseConfig) {
    this.courseConfig = courseConfig;
    this.activeShots = new Map();
    this.clock = new THREE.Clock();

    // Scene setup
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x87ceeb); // Sky blue

    // Camera setup
    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      10000
    );

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;

    // Controls setup
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;

    // Camera controller
    this.cameraController = new CameraController(this.camera, this.controls);

    // Setup lighting and course
    this.setupLighting();
    this.loadCourseTerrain();
  }

  /**
   * Setup scene lighting
   */
  private setupLighting(): void {
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    this.scene.add(ambientLight);

    // Directional light with shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(50, 100, 50);
    directionalLight.castShadow = true;
    this.scene.add(directionalLight);
  }

  /**
   * Load golf course terrain
   */
  private loadCourseTerrain(): void {
    const textureLoader = new THREE.TextureLoader();
    const courseTexture = textureLoader.load(this.courseConfig.textureUrl, () => {
      console.log('Golf course texture loaded');
    });
    courseTexture.colorSpace = THREE.SRGBColorSpace;

    // Create golf course plane
    const courseGeometry = new THREE.PlaneGeometry(
      this.courseConfig.width,
      this.courseConfig.height
    );
    const courseMaterial = new THREE.MeshStandardMaterial({
      map: courseTexture,
      side: THREE.DoubleSide,
    });
    const courseMesh = new THREE.Mesh(courseGeometry, courseMaterial);
    courseMesh.rotation.x = -Math.PI / 2; // Lay flat
    courseMesh.receiveShadow = true;
    this.scene.add(courseMesh);
  }

  /**
   * Add a shot to the scene
   */
  public addShot(shotData: ShotData): void {
    // Convert lat/lng to local coordinates
    const start = latLngToLocalFromObject(
      shotData.start,
      this.courseConfig.bounds,
      this.courseConfig.width,
      this.courseConfig.height
    );
    const end = latLngToLocalFromObject(
      shotData.end,
      this.courseConfig.bounds,
      this.courseConfig.width,
      this.courseConfig.height
    );

    // Create appropriate shot tracer
    const tracer = shotData.useTube !== false
      ? new TubeShotTracer(
          start,
          end,
          shotData.apexHeight,
          shotData.color,
          shotData.playerInfo,
          2 // tube radius
        )
      : new ShotTracer(
          start,
          end,
          shotData.apexHeight,
          shotData.color,
          shotData.playerInfo,
          5 // line width
        );

    // Add to scene WITH userData tags for removal
    const pathObject = tracer.getPathObject();
    pathObject.userData.shotId = shotData.id;
    this.scene.add(pathObject);

    const golfBallGroup = tracer.getGolfBallGroup();
    golfBallGroup.userData.shotId = shotData.id;
    this.scene.add(golfBallGroup);

    // Store in active shots
    this.activeShots.set(shotData.id, tracer);
  }

  /**
   * Remove a shot from the scene
   * Removes ALL objects associated with this shot (including old animation frames)
   */
  public removeShot(shotId: string): void {
    // Dispose tracer
    const tracer = this.activeShots.get(shotId);
    if (tracer) {
      tracer.dispose();
      this.activeShots.delete(shotId);
    }

    // Remove ALL scene objects tagged with this shotId
    const objectsToRemove = this.scene.children.filter(
      (child) => child.userData.shotId === shotId
    );

    objectsToRemove.forEach((object) => {
      this.scene.remove(object);
      // Dispose geometry/materials for meshes
      if (object instanceof THREE.Mesh) {
        object.geometry?.dispose();
        if (object.material instanceof THREE.Material) {
          object.material.dispose();
        }
      }
    });

    console.log(`[CourseScene] Removed shot ${shotId} (${objectsToRemove.length} objects)`);
  }

  /**
   * Clear all shots from the scene
   * Reuses removeShot() for consistent cleanup
   */
  public clearAllShots(): void {
    const shotIds = Array.from(this.activeShots.keys());
    shotIds.forEach((shotId) => this.removeShot(shotId));
    console.log('[CourseScene] All shots cleared');
  }

  /**
   * Get all active shots
   */
  public getActiveShots(): Map<string, BaseShotTracer> {
    return this.activeShots;
  }

  /**
   * Set camera to a preset
   */
  public setCameraPreset(preset: CameraPreset, animated: boolean = false): void {
    if (animated) {
      this.cameraController.animateTo(preset, 3);
    } else {
      this.cameraController.setPreset(preset);
    }
  }

  /**
   * Animate camera to a preset
   */
  public animateCameraTo(preset: CameraPreset, duration: number = 3): void {
    this.cameraController.animateTo(preset, duration);
  }

  /**
   * Get camera for screen projection calculations
   */
  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  /**
   * Get current camera settings (position and target)
   * Useful for capturing camera presets from the browser console
   */
  public getCameraSettings(): CameraPreset {
    return {
      position: {
        x: this.camera.position.x,
        y: this.camera.position.y,
        z: this.camera.position.z,
      },
      target: {
        x: this.controls.target.x,
        y: this.controls.target.y,
        z: this.controls.target.z,
      },
    };
  }

  /**
   * Move camera to specific position and target
   * @param position Camera position
   * @param target Look-at target
   * @param duration Animation duration in seconds
   */
  public moveCameraTo(
    position: { x: number; y: number; z: number },
    target: { x: number; y: number; z: number },
    duration: number = 3
  ): void {
    const preset: CameraPreset = { position, target };
    this.cameraController.animateTo(preset, duration);
  }

  /**
   * Render the scene (call in animation loop)
   */
  public render(): void {
    const deltaTime = this.clock.getDelta();

    // Update camera controller
    this.cameraController.update(deltaTime);

    // Update all active shot tracers
    this.activeShots.forEach((tracer, shotId) => {
      tracer.update(deltaTime);

      // Update path object if it changed (for tube geometry)
      const pathObject = tracer.getPathObject();
      if (!this.scene.children.includes(pathObject)) {
        // Tag new path object before adding
        pathObject.userData.shotId = shotId;
        this.scene.add(pathObject);
      }
    });

    // Render scene
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Handle window resize
   */
  public handleResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);

    // Update Line2 resolution for ShotTracer instances
    this.activeShots.forEach((tracer) => {
      if (tracer instanceof ShotTracer) {
        tracer.updateResolution(window.innerWidth, window.innerHeight);
      }
    });
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    // Dispose all shots
    this.activeShots.forEach((tracer) => tracer.dispose());
    this.activeShots.clear();

    // Dispose controls
    this.controls.dispose();

    // Dispose renderer
    this.renderer.dispose();

    // Traverse and dispose scene objects
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        if (object.material instanceof THREE.Material) {
          object.material.dispose();
        }
      }
    });
  }
}
